/**
 * FinalInsuranceDialog - Final insurance decision dialog (Approve/Deny/Modifications)
 * Extracted from ReferralsPage to reduce file size
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export function FinalInsuranceDialog({
  open,
  onOpenChange,
  decision,
  notes,
  setNotes,
  moveToNonAdmit,
  setMoveToNonAdmit,
  onSubmit,
  onCancel,
}) {
  const getTitle = () => {
    switch (decision) {
      case "denied":
        return "Insurance Payment Denial";
      case "modifications":
        return "Insurance Modifications Required";
      default:
        return "Insurance Payment Approval";
    }
  };

  const getDescription = () => {
    switch (decision) {
      case "denied":
        return "This will deny payment for services.";
      case "modifications":
        return "Specify the modifications required by insurance.";
      default:
        return "This will approve payment for services.";
    }
  };

  const getPlaceholder = () => {
    switch (decision) {
      case "denied":
        return "Reason for denial...";
      case "modifications":
        return "Required modifications...";
      default:
        return "Any notes...";
    }
  };

  const getButtonClass = () => {
    switch (decision) {
      case "denied":
        return "bg-red-600 hover:bg-red-700";
      case "modifications":
        return "bg-orange-600 hover:bg-orange-700";
      default:
        return "bg-emerald-600 hover:bg-emerald-700";
    }
  };

  const getButtonText = () => {
    switch (decision) {
      case "denied":
        return "Confirm Denial";
      case "modifications":
        return "Confirm Modifications";
      default:
        return "Confirm Approval";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription>{getDescription()}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={getPlaceholder()}
              rows={3}
              data-testid="final-insurance-notes"
            />
          </div>
          
          {/* Move to Non-Admit option - only show when denied */}
          {decision === "denied" && (
            <div className="flex items-center space-x-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <Checkbox
                id="final-move-to-non-admit"
                checked={moveToNonAdmit}
                onCheckedChange={setMoveToNonAdmit}
                data-testid="final-move-to-non-admit-checkbox"
              />
              <div className="flex-1">
                <label
                  htmlFor="final-move-to-non-admit"
                  className="text-sm font-medium text-orange-800 cursor-pointer"
                >
                  Move file to Non-Admit
                </label>
                <p className="text-xs text-orange-600">
                  This will close the referral and mark it as non-admitted
                </p>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className={getButtonClass()}
            onClick={onSubmit}
            data-testid="confirm-final-insurance-btn"
          >
            {getButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
